import * as React from "react"
import { format, isValid, parse } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value?: Date
  onValueChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  maxDate?: Date
  minDate?: Date
  className?: string
  inputClassName?: string
  inputStyle?: React.CSSProperties
}

export function DatePicker({
  value,
  onValueChange,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  maxDate,
  minDate,
  className,
  inputClassName,
  inputStyle,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [month, setMonth] = React.useState<Date>(() => value && isValid(value) ? value : new Date())

  // Sync input value with the date value
  React.useEffect(() => {
    if (value && isValid(value)) {
      setInputValue(format(value, "MM/dd/yyyy"))
      setMonth(value)
    } else {
      setInputValue("")
    }
  }, [value])

  // Drive the calendar's displayed month from what the user is typing,
  // so the popover follows the input instead of staying on the default month.
  const navigateMonthFromInput = (formatted: string) => {
    const digits = formatted.replace(/\D/g, "")
    if (digits.length < 2) return
    const mm = parseInt(digits.slice(0, 2), 10)
    if (!Number.isFinite(mm) || mm < 1 || mm > 12) return
    const yyyy = digits.length >= 8 ? parseInt(digits.slice(4, 8), 10) : month.getFullYear()
    if (!Number.isFinite(yyyy)) return
    const next = new Date(yyyy, mm - 1, 1)
    if (isValid(next)) setMonth(next)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    
    // Remove all non-numeric characters
    const digits = rawValue.replace(/\D/g, "")
    
    // Auto-format with slashes: MM/DD/YYYY
    let formatted = ""
    if (digits.length > 0) {
      formatted = digits.slice(0, 2)
    }
    if (digits.length >= 2) {
      formatted += "/"
    }
    if (digits.length > 2) {
      formatted += digits.slice(2, 4)
    }
    if (digits.length >= 4) {
      formatted += "/"
    }
    if (digits.length > 4) {
      formatted += digits.slice(4, 8)
    }
    
    setInputValue(formatted)
    navigateMonthFromInput(formatted)

    // Try to parse the date when complete (MM/DD/YYYY = 10 chars)
    if (formatted.length === 10) {
      const parsedDate = parse(formatted, "MM/dd/yyyy", new Date())
      if (isValid(parsedDate)) {
        // Check if date is within bounds
        if (minDate && parsedDate < minDate) return
        if (maxDate && parsedDate > maxDate) return
        onValueChange?.(parsedDate)
      }
    } else if (formatted === "") {
      onValueChange?.(undefined)
    }
  }

  const handleInputBlur = () => {
    // On blur, if the input doesn't match the current value, reset it
    if (value && isValid(value)) {
      setInputValue(format(value, "MM/dd/yyyy"))
    } else if (inputValue !== "") {
      // Try to parse one more time
      const parsedDate = parse(inputValue, "MM/dd/yyyy", new Date())
      if (isValid(parsedDate)) {
        if (minDate && parsedDate < minDate) {
          setInputValue("")
          return
        }
        if (maxDate && parsedDate > maxDate) {
          setInputValue("")
          return
        }
        onValueChange?.(parsedDate)
      } else {
        setInputValue("")
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "relative flex items-center w-full",
            className
          )}
        >
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn("pr-10 h-10", inputClassName)}
            style={inputStyle}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
          />
          <CalendarIcon className="absolute right-3 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0" 
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        avoidCollisions={true}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ zIndex: 99999 }}
      >
        <Calendar
          mode="single"
          selected={value}
          month={month}
          onMonthChange={setMonth}
          onSelect={(date) => {
            onValueChange?.(date)
            if (date) setMonth(date)
            setOpen(false)
          }}
          disabled={disabled}
          maxDate={maxDate ? new Date(maxDate) : undefined}
          minDate={minDate ? new Date(minDate) : undefined}
        />
      </PopoverContent>
    </Popover>
  )
} 